import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BriefcaseBusiness } from "lucide-react";
import { fetchStockSvgThunk } from "../../redux/iconStocks/iconStocksActionThunks";

export default function StockLogo({ symbol, name, size = "w-8 h-8" }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Select logo state
  const stockIcons = useSelector((state) => state.stockLogo.stockIcons || {});
  const stockIconsLoading = useSelector((state) => state.stockLogo.loading);

  const icon = stockIcons[symbol];

  // Only fetch if we don't have the icon and we're not currently loading this specific symbol
  useEffect(() => {
    if (symbol && !icon && !stockIconsLoading) {
      dispatch(fetchStockSvgThunk(symbol));
    }
  }, [symbol, icon, stockIconsLoading, dispatch]);

  // Generate placeholder letters from symbol
  const getPlaceholderText = (symbolStr) => {
    if (!symbolStr) return "??";
    // Remove $ if present and take first 2 characters
    const cleanSymbol = symbolStr.replace('$', '').toUpperCase();
    return cleanSymbol.substring(0, 2);
  };

  // Generate a consistent color based on symbol for variety
  const getPlaceholderColor = (symbolStr) => {
    if (!symbolStr) return 'bg-gray-700';
    
    // Brand triad (violet/indigo/blue) leads, rounded out with enough
    // additional hues for real per-symbol variety across many tickers.
    const colors = [
      'bg-violet-600/80',
      'bg-indigo-600/80',
      'bg-blue-600/80',
      'bg-emerald-600/80',
      'bg-pink-600/80',
      'bg-amber-600/80',
      'bg-cyan-600/80',
      'bg-rose-600/80',
    ];
    
    // Simple hash to pick consistent color
    const hash = symbolStr.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // 1. Loading skeleton - only show when actively loading and no cached data
  if (stockIconsLoading && !icon) {
    return (
      <div
        className={`${size} bg-gray-700/50 rounded-md animate-pulse flex items-center justify-center border border-gray-600/50`}
      >
        <div className="w-4 h-4 bg-gray-600 rounded animate-pulse" />
      </div>
    );
  }

  // 2. Inline SVG - highest priority. `id != null` distinguishes a real,
  // stored logo from the backend's synthesized gray-box fallback template
  // (GET /stocks-svg/{symbol} always returns a truthy `svg` field either
  // way, with `id: null` on the fallback -- without this check, ~48% of
  // tickers with no real logo on file rendered a flat #ccc box instead of
  // ever reaching the colorful initials tier below, which was built to be
  // the "no logo" case but was unreachable).
  if (icon?.svg && icon?.id != null) {
    const safeSvg = icon.svg.replace("xMidYMid Meet", "xMidYMid meet");
    return (
      <span
        className={`${size} inline-flex items-center justify-center rounded-md bg-white/10 p-1 border border-white/20
                    [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full overflow-hidden`}
        dangerouslySetInnerHTML={{ __html: safeSvg }}
        title={t('stocks.logo.svgTitle', { symbol })}
      />
    );
  }

  // 3. Logo URL fallback
  if (icon?.logo_url) {
    return (
      <img
        src={icon.logo_url}
        alt={t('stocks.logo.imgAlt', { symbol })}
        className={`${size} object-contain rounded-md bg-white/10 border border-white/20`}
        onError={(e) => {
          // If image fails to load, hide it and let component fall through to text fallback
          e.target.style.display = 'none';
        }}
      />
    );
  }

  // 4. Text fallback with symbol letters - more prominent and colorful
  if (symbol) {
    const placeholderText = getPlaceholderText(symbol);
    const bgColor = getPlaceholderColor(symbol);
    
    return (
      <div
        className={`${size} rounded-md ${bgColor} flex items-center justify-center text-white font-bold border border-white/30 shadow-sm`}
        title={`${symbol}${name ? ` - ${name}` : ''}`}
      >
        <span className="text-xs leading-none select-none">
          {placeholderText}
        </span>
      </div>
    );
  }

  // 5. Absolute fallback icon
  return (
    <div className={`${size} rounded-md bg-gray-700/80 border border-gray-600/50 flex items-center justify-center`}>
      <BriefcaseBusiness className="w-4 h-4 text-gray-400" />
    </div>
  );
}